
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function HermanusPage() {
  useEffect(() => {
    try {
      document.title =
        'Hermanus Painters Western Cape | Whale Coast Painting Services | Marine Protection';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Professional painting services in Hermanus, Western Cape. Expert coastal painting, marine-grade protection, and whale coast property maintenance. Serving Hermanus and surrounding areas.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Hermanus painters, whale coast painting, marine protection Western Cape'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/hermanus`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Hermanus Painters Western Cape',
        description: 'Professional painting services in Hermanus, Western Cape',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/hermanus`,
        areaServed: {
          '@type': 'Place',
          name: 'Hermanus, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -34.4187,
            longitude: 19.2345,
          },
          geoRadius: '25000',
        },
      });
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error setting page meta tags or schema script:', error);
    }

    return () => {
      try {
        const existingScript = document.querySelector(
          'script[type="application/ld+json"]'
        );
        if (existingScript && existingScript.textContent?.includes('Hermanus Painters')) {
          existingScript.remove();
        }
      } catch (error) {
        console.error('Error cleaning up schema script:', error);
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d0c664ea1e70ff51e07baebb78c468cd.jpg"
            alt="Professional painting services in Hermanus, Western Cape"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Premier <span className="text-yellow-400">Hermanus Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional painting services for the Whale Coast. Specialized marine-grade protection and expert
              craftsmanship for Hermanus properties with stunning ocean views.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Painting Services in Hermanus
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert painting solutions for Hermanus properties, from luxury coastal homes to commercial
              establishments, designed to withstand the unique Whale Coast environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-heart-line',
                title: 'Coastal Residential',
                description:
                  'Specialized painting for coastal homes with marine-grade protection against salt air and ocean conditions.',
                features: [
                  'Ocean view properties',
                  'Salt air protection',
                  'UV resistance',
                  'Weather sealing',
                ],
              },
              {
                icon: 'ri-hotel-line',
                title: 'Hospitality & Tourism',
                description:
                  'Professional painting for hotels, guesthouses, and tourism facilities serving the Whale Coast visitors.',
                features: ['Hotels & lodges', 'Guest houses', 'Restaurants', 'Tourism facilities'],
              },
              {
                icon: 'ri-water-percent-line',
                title: 'Marine Protection',
                description:
                  'Advanced marine-grade coatings and waterproofing systems designed for harsh coastal environments.',
                features: [
                  'Marine-grade coatings',
                  'Waterproofing',
                  'Storm protection',
                  'Long-term durability',
                ],
              },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-2"></i> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Hermanus Properties Choose Our Whale Coast Expertise
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Whale Coast Specialization',
                    description:
                      "Extensive experience with Hermanus' unique coastal conditions, from ocean spray to seasonal weather patterns.",
                  },
                  {
                    title: 'Tourism Industry Knowledge',
                    description:
                      'Understanding of hospitality needs with flexible scheduling to minimize disruption during peak whale season.',
                  },
                  {
                    title: 'Marine-Grade Solutions',
                    description:
                      'Premium coastal protection systems designed to withstand the harsh marine environment of Walker Bay.',
                  },
                  {
                    title: 'Local Community Focus',
                    description:
                      'Committed to serving the Hermanus community with reliable, professional service and local expertise.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Hermanus%20coastal%20property%2C%20whale%20coast%20home%20maintenance%2C%20marine-grade%20painting%20equipment%2C%20Walker%20Bay%20ocean%20views%2C%20Western%20Cape%20coastal%20painting%20services&width=600&height=700&seq=hermanus-team-work&orientation=portrait"
                alt="Professional painting services in Hermanus"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Recent Hermanus Whale Coast Projects</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our recent painting projects in Hermanus, demonstrating our expertise in coastal
              protection and quality craftsmanship.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Luxury Ocean View Villa',
                location: 'Hermanus Heights',
                description:
                  'Complete exterior renovation of a luxury villa with panoramic Walker Bay views.',
                image:
                  'https://readdy.ai/api/search-image?query=Luxury%20ocean%20view%20villa%20painting%20in%20Hermanus%2C%20high-end%20coastal%20home%20with%20Walker%20Bay%20views%2C%20premium%20exterior%20painting%2C%20modern%20coastal%20architecture%20Western%20Cape&width=400&height=300&seq=hermanus-project-1&orientation=landscape',
              },
              {
                title: 'Boutique Whale Watching Lodge',
                location: 'Hermanus Waterfront',
                description:
                  'Comprehensive painting renovation for a popular whale watching accommodation facility.',
                image:
                  'https://readdy.ai/api/search-image?query=Boutique%20whale%20watching%20lodge%20painting%20in%20Hermanus%2C%20tourism%20accommodation%20facility%2C%20hospitality%20property%20painting%2C%20coastal%20lodge%20with%20ocean%20views%20Western%20Cape&width=400&height=300&seq=hermanus-project-2&orientation=landscape',
              },
              {
                title: "Historic Fisherman's Cottage",
                location: 'Old Harbour Hermanus',
                description:
                  "Careful restoration of a historic fisherman\'s cottage maintaining authentic coastal character.",
                image:
                  'https://readdy.ai/api/search-image?query=Historic%20fishermans%20cottage%20restoration%20painting%20in%20Hermanus%2C%20traditional%20coastal%20architecture%2C%20heritage%20property%20painting%2C%20authentic%20whale%20coast%20character%20Western%20Cape&width=400&height=300&seq=hermanus-project-3&orientation=landscape',
              },
            ].map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img src={project.image} alt={project.title} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What Hermanus Clients Say</h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied property owners and businesses in Hermanus
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Jennifer Williams',
                location: 'Hermanus Heights',
                rating: 5,
                text: 'Exceptional work on our ocean view home. The marine-grade coatings have held up beautifully against the coastal conditions. Professional service throughout.',
              },
              {
                name: 'Peter van Zyl',
                location: 'Hermanus Waterfront',
                rating: 5,
                text: 'Outstanding painting service for our guesthouse. They worked around our guests during whale season with minimal disruption. Highly recommended for tourism properties.',
              },
              {
                name: 'Susan Clarke',
                location: 'Old Harbour',
                rating: 5,
                text: 'Beautiful restoration of our heritage cottage. They understood the importance of maintaining the authentic coastal character while providing modern protection.',
              },
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-5">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Enhance Your Hermanus Whale Coast Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for expert coastal painting consultation and marine-grade protection. Experience the
            difference of specialized Whale Coast painting services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
